/**
 * Summarizes action-plan progress for Risk Pattern Review (decision surface).
 * This module intentionally derives UI state only from explicit workflow data, not from
 * "user clicked a surface" heuristics.
 */

export type OperationalFollowThroughStage =
  | 'not_started'
  | 'copilot_opened'
  | 'runbook_started'
  | 'in_progress'
  | 'completed'
  | 'followup_scheduled'
  | 'measured';

export type FollowThroughItemKind =
  | 'email'
  | 'sms'
  | 'odoo_task'
  | 'notification'
  | 'followup_review';

export type FollowThroughItemStatus = 'required' | 'completed' | 'not_required' | 'pending';

export type FollowThroughItem = {
  label: string;
  kind: FollowThroughItemKind;
  status: FollowThroughItemStatus;
};

export type OperationalFollowThroughNextAction = {
  label: string;
  detail?: string;
  targetSurface?: 'copilot' | 'runbook' | 'incidents' | 'followup_review' | 'measurement';
};

export type OperationalFollowThroughModel = {
  stage: OperationalFollowThroughStage;
  statusLabel: string;
  requiredCount: number;
  completedCount: number;
  items: FollowThroughItem[];
  nextBestAction: OperationalFollowThroughNextAction;
  summaryLine?: string;
};

export type RunbookFollowThroughStatus = 'idle' | 'started' | 'in_progress' | 'completed' | undefined;
export type CopilotFollowThroughStatus = 'none' | 'opened' | undefined;
export type FollowupReviewFollowThroughStatus = 'none' | 'pending' | 'scheduled' | 'done' | undefined;

export type OperationalFollowThroughInput = {
  runbookStatus?: RunbookFollowThroughStatus;
  copilotStatus?: CopilotFollowThroughStatus;
  runbookTitle?: string | null;
  requiredChannels?: FollowThroughItemKind[];
  completedChannels?: FollowThroughItemKind[];
  followupReviewStatus?: FollowupReviewFollowThroughStatus;
  channelOverrides?: Partial<Record<FollowThroughItemKind, FollowThroughItemStatus>>;
  measured?: boolean;
  actionTargets?: {
    runbookLabel?: string | null;
    copilotLabel?: string | null;
    incidentsLabel?: string | null;
    followupReviewLabel?: string | null;
    measurementLabel?: string | null;
  };
};

const DEFAULT_LABELS: Record<FollowThroughItemKind, string> = {
  odoo_task: 'Create Odoo task',
  notification: 'Notify safety supervisor',
  followup_review: 'Schedule follow-up review',
  email: 'Supervisor email',
  sms: 'SMS alert',
};

function defaultRequiredKinds(): FollowThroughItemKind[] {
  return ['odoo_task', 'notification', 'followup_review'];
}

function nonEmpty(value?: string | null): string {
  return String(value || '').trim();
}

function buildItemList(input: OperationalFollowThroughInput): FollowThroughItem[] {
  const required = (input.requiredChannels?.length ? input.requiredChannels : defaultRequiredKinds()).map((k) =>
    String(k).trim()
  ) as FollowThroughItemKind[];
  const completed = new Set(
    (input.completedChannels || []).map((k) => String(k).trim()).filter(Boolean) as FollowThroughItemKind[]
  );
  const allKinds: FollowThroughItemKind[] = ['email', 'sms', 'odoo_task', 'notification', 'followup_review'];
  const requiredSet = new Set(required);

  return allKinds.map((kind) => {
    const override = input.channelOverrides?.[kind];
    const isRequired = requiredSet.has(kind);
    let status: FollowThroughItemStatus;
    if (override != null) {
      status = override;
    } else if (!isRequired && (kind === 'email' || kind === 'sms')) {
      status = 'not_required';
    } else if (completed.has(kind)) {
      status = 'completed';
    } else if (
      isRequired
      && (input.runbookStatus === 'started' || input.runbookStatus === 'in_progress' || input.runbookStatus === 'completed')
    ) {
      status = kind === 'followup_review' && input.followupReviewStatus === 'pending' ? 'pending' : 'pending';
    } else if (kind === 'followup_review' && input.followupReviewStatus === 'pending') {
      status = 'pending';
    } else if (isRequired) {
      status = 'required';
    } else {
      status = 'not_required';
    }
    return {
      kind,
      label: DEFAULT_LABELS[kind],
      status,
    };
  });
}

function obligationItems(items: FollowThroughItem[]): FollowThroughItem[] {
  return items.filter((i) => i.status !== 'not_required');
}

function deriveStage(
  input: OperationalFollowThroughInput,
  requiredCount: number,
  completedCount: number
): OperationalFollowThroughStage {
  if (input.measured) return 'measured';

  const followupStatus = input.followupReviewStatus;
  if (requiredCount > 0 && completedCount >= requiredCount && followupStatus !== 'scheduled' && followupStatus !== 'pending') {
    return 'completed';
  }
  if (followupStatus === 'scheduled') return 'followup_scheduled';
  if (completedCount > 0) return 'in_progress';
  if (input.runbookStatus === 'in_progress') return 'in_progress';
  if (input.runbookStatus === 'started' || input.runbookStatus === 'completed') return 'runbook_started';
  if (input.copilotStatus === 'opened') return 'copilot_opened';
  return 'not_started';
}

function deriveStatusLabel(stage: OperationalFollowThroughStage): string {
  switch (stage) {
    case 'not_started':
      return 'Recommendation ready';
    case 'copilot_opened':
      return 'Runbook not started';
    case 'runbook_started':
    case 'in_progress':
    case 'completed':
      return 'Action in progress';
    case 'followup_scheduled':
      return 'Action scheduled';
    case 'measured':
      return 'Measured';
    default:
      return 'Recommendation ready';
  }
}

function deriveSummaryLine(stage: OperationalFollowThroughStage, input: OperationalFollowThroughInput): string | undefined {
  if (stage === 'measured') {
    return 'This recommendation has baseline or impact-tracking data recorded for the program.';
  }
  if (stage === 'followup_scheduled') {
    return 'A next review is scheduled and ready for the next comparison window.';
  }
  if (stage === 'not_started') {
    return 'No action workflow has been started for this recommendation yet.';
  }
  if (stage === 'copilot_opened') {
    return 'Context has been prepared, but the operational workflow has not been started yet.';
  }
  const title = nonEmpty(input.runbookTitle);
  if (stage === 'runbook_started' || stage === 'in_progress') {
    return title
      ? `Runbook: ${title}`
      : 'Operational follow-up is in progress in Runbooks and connected tools.';
  }
  if (stage === 'completed') {
    return 'All required actions are complete; measure the next review window when ready.';
  }
  return undefined;
}

function fallbackAction(label: string, detail: string, targetSurface?: OperationalFollowThroughNextAction['targetSurface']): OperationalFollowThroughNextAction {
  return { label, detail, targetSurface };
}

function deriveNextBestAction(
  stage: OperationalFollowThroughStage,
  input: OperationalFollowThroughInput,
  items: FollowThroughItem[]
): OperationalFollowThroughNextAction {
  const targets = input.actionTargets || {};
  const runbookLabel = nonEmpty(targets.runbookLabel);
  const copilotLabel = nonEmpty(targets.copilotLabel);
  const incidentsLabel = nonEmpty(targets.incidentsLabel);
  const followupReviewLabel = nonEmpty(targets.followupReviewLabel);
  const measurementLabel = nonEmpty(targets.measurementLabel);
  const firstPending = items.find((i) => i.status === 'pending' || i.status === 'required');

  if (stage === 'measured') {
    return fallbackAction(
      measurementLabel || 'Review next window',
      'Compare the next evidence package against this baseline in Risk Review.',
      measurementLabel ? 'measurement' : undefined,
    );
  }
  if (stage === 'followup_scheduled') {
    return fallbackAction(
      measurementLabel || 'Prepare measurement',
      'Use the scheduled follow-up review to compare the next evidence window.',
      measurementLabel ? 'measurement' : undefined,
    );
  }

  if (firstPending?.kind === 'followup_review') {
    return fallbackAction(
      followupReviewLabel || 'Schedule follow-up review',
      'Lock the next review window before marking this recommendation complete.',
      followupReviewLabel ? 'followup_review' : undefined,
    );
  }

  if (stage === 'copilot_opened' || stage === 'not_started' || firstPending?.kind === 'odoo_task' || firstPending?.kind === 'notification') {
    if (runbookLabel) {
      return fallbackAction(
        runbookLabel,
        'Start or continue the runbook to create tasks, notifications, and tracked follow-up.',
        'runbook',
      );
    }
    if (incidentsLabel) {
      return fallbackAction(
        incidentsLabel,
        'Review supporting incidents and move execution into the right workflow from there.',
        'incidents',
      );
    }
    if (copilotLabel) {
      return fallbackAction(
        copilotLabel,
        'Use Copilot for context only, then move execution into the operational workflow.',
        'copilot',
      );
    }
  }

  if (copilotLabel && stage === 'runbook_started') {
    return fallbackAction(
      copilotLabel,
      'Use Copilot to brief the current recommendation while execution continues elsewhere.',
      'copilot',
    );
  }

  return fallbackAction(
    measurementLabel || runbookLabel || followupReviewLabel || incidentsLabel || 'Review recommendation status',
    'Operational execution stays in Runbooks, messaging tools, and follow-up workflows.',
    measurementLabel ? 'measurement' : runbookLabel ? 'runbook' : followupReviewLabel ? 'followup_review' : incidentsLabel ? 'incidents' : undefined,
  );
}

export function buildOperationalFollowThrough(input: OperationalFollowThroughInput): OperationalFollowThroughModel {
  const items = buildItemList(input);
  const oblig = obligationItems(items);
  const requiredCount = oblig.length;
  const completedCount = oblig.filter((i) => i.status === 'completed').length;
  const stage = deriveStage(input, requiredCount, completedCount);
  const statusLabel = deriveStatusLabel(stage);
  const summaryLine = deriveSummaryLine(stage, input);
  const nextBestAction = deriveNextBestAction(stage, input, items);

  return {
    stage,
    statusLabel,
    requiredCount,
    completedCount,
    items,
    nextBestAction,
    summaryLine,
  };
}
